// src/pages/PricingPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { IoCheckmarkCircle } from 'react-icons/io5';
import FinalCTA from '../components/FinalCTA';
import { fetchPricingPlans } from '../services/apiService';

// --- Conversion & Formatting Helpers ---
const CONVERSION_RATE_INR_TO_USD = 83;

const formatCurrency = (price, currency = 'INR') => {
    if (typeof price !== 'number') return price; // For "Custom"

    if (currency === 'INR') {
        const priceInUsd = Math.round((price / 100) / CONVERSION_RATE_INR_TO_USD);
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
        }).format(priceInUsd);
    }

    // Fallback for other currencies (or if currency is not INR)
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
    }).format(price / 100);
};

const transformApiPlan = (planData, planType) => {
    const tier = planData[planType];
    if (!tier) return null;

    const priceToShow = planType === 'yearly' && typeof tier.plan_price === 'number'
        ? tier.plan_price / 12
        : tier.plan_price;

    return {
        name: tier.plan_name,
        price: formatCurrency(priceToShow, tier.plan_currency),
        description: tier.plan_description,
        features: [
            `${tier.brands_limit} Brand Profiles`,
            `${tier.profiles_limit} Social Profiles per Brand`,
            `${tier.posts_per_month_limit} Posts per Month`,
            `${tier.storage_gb_limit} GB Storage`,
            `${tier.ai_social_post_credits_limit} AI Post Credits/mo`,
            tier.support_level ? `${tier.support_level} Support` : 'Community Support',
        ],
        href: planData.isCustom ? "/schedule-demo" : "https://app.oolook.in/signup",
        _raw: tier,
    };
};

// --- Static Data ---
const comparisonFeatures = [
    { category: 'Core Features', items: [
        { name: 'Brand Profiles', key: 'brands_limit' },
        { name: 'Social Profiles per Brand', key: 'profiles_limit' },
        { name: 'Posts per Month', key: 'posts_per_month_limit' },
    ]},
    { category: 'AI Credits', items: [
        { name: 'AI Social Post Credits', key: 'ai_social_post_credits_limit', suffix: '/mo' },
        { name: 'AI Caption Credits', key: 'ai_caption_credits_limit', suffix: '/mo' },
        { name: 'AI Image Credits', key: 'ai_image_credits_limit', suffix: '/mo' },
    ]},
    { category: 'Team & Support', items: [
        { name: 'Storage', key: 'storage_gb_limit', suffix: ' GB' },
        { name: 'Supported Channels', key: 'supported_channels' },
        { name: 'Support Level', key: 'support_level' },
    ]},
];

// --- Component ---
const PricingPage = () => {
    const [frequency, setFrequency] = useState('monthly');
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTier, setActiveTier] = useState('');
    const [maxDiscount, setMaxDiscount] = useState('');

    useEffect(() => {
        const loadPlans = async () => {
            try {
                const apiPlans = await fetchPricingPlans();

                // Sort plans: Free -> Paid (low to high) -> Custom
                apiPlans.sort((a, b) => {
                    if (a.isFree) return -1;
                    if (b.isFree) return 1;
                    if (a.isCustom) return 1;
                    if (b.isCustom) return -1;
                    return (a.monthly?.plan_price || 0) - (b.monthly?.plan_price || 0);
                });

                const processedPlans = [];
                let calculatedMaxDiscount = 0;

                apiPlans.forEach(p => {
                    if (p.monthly && p.yearly) {
                        const discount = Math.round(((p.monthly.plan_price * 12 - p.yearly.plan_price) / (p.monthly.plan_price * 12)) * 100);
                        if (discount > calculatedMaxDiscount) calculatedMaxDiscount = discount;
                    }
                });
                if (calculatedMaxDiscount > 0) setMaxDiscount(calculatedMaxDiscount);

                const freePlan = apiPlans.find(p => p.isFree);
                const enterprisePlan = apiPlans.find(p => p.isCustom);
                const paidPlans = apiPlans.filter(p => !p.isFree && !p.isCustom);

                if (freePlan) {
                    processedPlans.push({ id: 'free', name: 'Free', isGroup: false, mostPopular: false, ...transformApiPlan(freePlan, 'monthly') });
                }

                if (paidPlans.length >= 2) {
                    const firstPaidPlan = paidPlans[0];
                    const secondPaidPlan = paidPlans[1];
                    setActiveTier(firstPaidPlan.base_plan_name); // Set initial active tier

                    processedPlans.push({
                        id: 'grouped_plan',
                        name: 'Paid Plans',
                        isGroup: true,
                        mostPopular: true,
                        options: {
                            [firstPaidPlan.base_plan_name]: {
                                monthly: transformApiPlan(firstPaidPlan, 'monthly'),
                                annually: transformApiPlan(firstPaidPlan, 'yearly'),
                            },
                            [secondPaidPlan.base_plan_name]: {
                                monthly: transformApiPlan(secondPaidPlan, 'monthly'),
                                annually: transformApiPlan(secondPaidPlan, 'yearly'),
                            },
                        },
                        // Store names for the toggle buttons
                        groupNames: [firstPaidPlan.base_plan_name, secondPaidPlan.base_plan_name],
                    });
                } else { // Handle case with 0 or 1 paid plans
                    paidPlans.forEach(p => {
                        processedPlans.push({ id: p.base_plan_name, isGroup: false, mostPopular: true, ...transformApiPlan(p, 'monthly') });
                    });
                }

                if (enterprisePlan) {
                    processedPlans.push({ id: 'enterprise', name: 'Enterprise', isGroup: false, mostPopular: false, ...transformApiPlan(enterprisePlan, 'monthly') });
                }

                setPlans(processedPlans);
            } catch (err) {
                setError(err.message || 'Could not load pricing plans.');
            } finally {
                setLoading(false);
            }
        };
        loadPlans();
    }, []);

    const allTiersForTable = plans.flatMap(p => {
        if (p.isGroup) {
            return p.groupNames.map(name => ({
                name,
                ...p.options[name].monthly._raw,
            }));
        }
        return { name: p.name, ...p._raw };
    }).filter(p => p && p.name);

    return (
        <>
            <SEO
                title="Pricing Plans | Oolook"
                description="Simple, transparent pricing for Oolook's AI social media platform. Start for free, then choose a plan that scales with you. No hidden fees."
            />
            <main className="bg-dark-bg text-dark-text-secondary animate-fade-in">
                <div className="relative isolate overflow-hidden">
                    <div className="px-6 pt-24 pb-16 text-center sm:pt-32 lg:px-8">
                        <h1 className="text-4xl font-bold tracking-tight text-dark-text-primary sm:text-6xl">Simple, transparent pricing</h1>
                        <p className="mt-6 text-lg leading-8 text-dark-text-secondary">
                            Choose the plan that's right for your goals. Start for free, no credit card required.
                        </p>
                        <div className="mt-10 flex justify-center">
                            <div className="flex rounded-full p-1 bg-dark-card/60 border border-white/10">
                                <button onClick={() => setFrequency('monthly')} className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors ${frequency === 'monthly' ? 'bg-dark-accent-start text-white' : 'text-dark-text-secondary'}`}>
                                    Monthly billing
                                </button>
                                <button onClick={() => setFrequency('annually')} className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors ${frequency === 'annually' ? 'bg-dark-accent-start text-white' : 'text-dark-text-secondary'}`}>
                                    Annually {maxDiscount && `(Save up to ${maxDiscount}%)`}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flow-root pb-24 sm:pb-32">
                        <div className="mx-auto max-w-7xl px-6 lg:px-8">
                            {loading && <p className="text-center">Loading plans...</p>}
                            {error && <p className="text-center text-red-400">{error}</p>}
                            {!loading && !error && (
                                <div className="isolate mx-auto grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                                    {plans.map((plan) => {
                                        const displayData = plan.isGroup ? plan.options[activeTier][frequency === 'annually' ? 'annually' : 'monthly'] : plan;
                                        const yearlyRawData = plan.isGroup ? plan.options[activeTier].annually?._raw : null;

                                        return (
                                            <div key={plan.id} className={`flex flex-col rounded-3xl p-8 ring-1 transition-all duration-300 ease-in-out ${plan.mostPopular ? 'ring-2 ring-dark-accent-start bg-dark-card' : 'ring-white/10 bg-dark-card/40'}`}>
                                                {plan.isGroup && (
                                                    <div className="mb-4 flex self-center rounded-full p-1 bg-dark-bg/50 border border-white/10">
                                                        {plan.groupNames.map(name => (
                                                            <button key={name} onClick={() => setActiveTier(name)} className={`px-3 py-1 text-xs font-semibold rounded-full ${activeTier === name ? 'bg-dark-accent-start/80 text-white' : 'text-dark-text-secondary'}`}>{name}</button>
                                                        ))}
                                                    </div>
                                                )}
                                                <h3 className="text-lg font-semibold leading-8 text-dark-text-primary">{displayData.name}</h3>
                                                <p className="mt-4 text-sm leading-6 text-dark-text-secondary">{displayData.description}</p>
                                                <p className="mt-6 flex items-baseline gap-x-1">
                                                    <span className="text-4xl font-bold tracking-tight text-dark-text-primary">{displayData.price}</span>
                                                    {displayData.price !== 'Free' && displayData.price !== 'Custom' && (
                                                        <span className="text-sm font-semibold leading-6 text-dark-text-secondary">/month</span>
                                                    )}
                                                </p>
                                                {frequency === 'annually' && yearlyRawData && (
                                                    <p className="mt-1 text-xs text-dark-text-secondary/70">
                                                        Billed as {formatCurrency(yearlyRawData.plan_price, yearlyRawData.plan_currency)} per year
                                                    </p>
                                                )}
                                                <Link to={displayData.href} className={`mt-6 block rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 ${plan.mostPopular ? 'bg-dark-accent-start text-white shadow-sm hover:opacity-90' : 'bg-white/10 text-white hover:bg-white/20'}`}>
                                                    {plan.id === "enterprise" ? "Contact Sales" : "Get Started"}
                                                </Link>
                                                <ul className="mt-8 space-y-3 text-sm leading-6 text-dark-text-secondary flex-1">
                                                    {displayData.features.map(feature => (
                                                        <li key={feature} className="flex gap-x-3">
                                                            <IoCheckmarkCircle className="h-6 w-5 flex-none text-dark-accent-start" />
                                                            {feature}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {!loading && !error && allTiersForTable.length > 0 && (
                        <div className="mx-auto max-w-7xl px-6 lg:px-8 pb-24 sm:pb-32">
                            <h2 className="text-center text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">Compare all features</h2>
                            <div className="mt-16 overflow-hidden rounded-3xl bg-dark-card/40 ring-1 ring-white/10">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="text-left">
                                            <tr>
                                                <th scope="col" className="px-6 py-4 font-semibold text-dark-text-primary w-1/4"></th>
                                                {allTiersForTable.map(tier => <th key={tier.plan_id || tier.name} scope="col" className="px-6 py-4 text-center font-semibold text-dark-text-primary">{tier.plan_name}</th>)}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-white/5">
                                            {comparisonFeatures.map(section => (
                                                <React.Fragment key={section.category}>
                                                    <tr><th colSpan={allTiersForTable.length + 1} className="px-6 py-3 text-base font-semibold text-dark-text-primary bg-white/5">{section.category}</th></tr>
                                                    {section.items.map(item => (
                                                        <tr key={item.name}>
                                                            <td className="px-6 py-4 text-dark-text-secondary">{item.name}</td>
                                                            {allTiersForTable.map(tier => (
                                                                <td key={tier.plan_id || tier.name} className="px-6 py-4 text-center">
                                                                    <span className="text-dark-text-secondary">
                                                                        {Array.isArray(tier[item.key]) ? tier[item.key].join(', ') : (tier[item.key] ?? '-')}
                                                                        {tier[item.key] && item.suffix}
                                                                    </span>
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="mt-8 text-center">
                                <Link to="/schedule-demo" className="text-sm font-semibold leading-6 text-dark-accent-start hover:text-dark-accent-end">
                                    Still have questions? Talk to our team <span aria-hidden="true">&rarr;</span>
                                </Link>
                            </div>
                        </div>
                    )}
                    <FinalCTA />
                </div>
            </main>
        </>
    );
};

export default PricingPage;